import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ScrollText, HeartHandshake, Mail, Trash2, Loader2 } from "lucide-react";
import { useSeo } from "@/components/VIP";
import { api } from "../lib/api";
import { MiniMarkdown } from "../lib/miniMd";

const ICONS = { shield: ShieldCheck, scroll: ScrollText, heart: HeartHandshake, trash: Trash2 };
const SUPPORT_EMAIL = "gulfcoastclub@gmail.com";

// Render an intro paragraph or short line that may contain the same markdown-lite
// inline tokens ([link](url), **bold**) but is displayed as a single paragraph.
function InlineText({ text }) {
  return <MiniMarkdown text={text} className="!text-zinc-300 !space-y-0" />;
}

function LegalPage({ slug, testId, fallbackTitle }) {
  const [doc, setDoc] = useState(undefined);
  useEffect(() => {
    setDoc(undefined);
    api.get(`/legal/${slug}`).then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, [slug]);

  useSeo(
    `${doc?.title || fallbackTitle} | Gulf Coast VIP`,
    doc?.intro?.slice(0, 155) || `Gulf Coast VIP ${fallbackTitle.toLowerCase()}.`
  );

  if (doc === undefined) return (
    <div className="py-40 flex justify-center text-zinc-500" data-testid={`${testId}-loading`}>
      <Loader2 className="animate-spin" />
    </div>
  );
  if (!doc) return (
    <div className="py-40 text-center text-zinc-400" data-testid={`${testId}-error`}>
      <p className="font-display text-3xl">Page unavailable</p>
      <p className="text-sm text-zinc-500 mt-2">Please try again shortly.</p>
    </div>
  );

  const Icon = ICONS[doc.icon] || ShieldCheck;
  return (
    <div className="px-6 md:px-12 lg:px-20 py-14 min-h-screen max-w-4xl mx-auto" data-testid={testId}>
      <p className="overline text-rose-400 flex items-center gap-2"><Icon size={14} /> {doc.overline}</p>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mt-1">{doc.title}</h1>
      <p className="text-xs text-zinc-500 mt-2">Last updated: {doc.updated_label || "recently"}</p>
      {doc.intro && <div className="mt-5 leading-relaxed"><InlineText text={doc.intro} /></div>}
      <div className="mt-8 space-y-8">
        {(doc.sections || []).map((s, i) => (
          <section key={i} data-testid={`${testId}-section-${i}`}>
            {s.title && <h2 className="font-display font-bold text-lg mb-2 text-white">{s.title}</h2>}
            <MiniMarkdown text={s.body} />
          </section>
        ))}
      </div>
      {slug === "cancel-membership" && (
        <div className="mt-8 rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5 flex items-start gap-3">
          <Mail className="text-rose-400 shrink-0 mt-0.5" size={18} />
          <p className="text-sm text-zinc-300">Need help with anything on this page? Email <a className="text-rose-400 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> — a real person reads every message.</p>
        </div>
      )}
      <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap gap-4 text-sm text-zinc-400">
        <Link to="/privacy-policy" className="hover:text-white underline">Privacy Policy</Link>
        <Link to="/terms-of-use" className="hover:text-white underline">Terms of Use</Link>
        <Link to="/cancel-membership" className="hover:text-white underline">Cancel Membership</Link>
        <Link to="/data-deletion" className="hover:text-white underline">Data Deletion</Link>
      </div>
    </div>
  );
}

export function PrivacyPolicy() { return <LegalPage slug="privacy-policy" testId="privacy-policy-page" fallbackTitle="Privacy Policy" />; }
export function TermsOfUse() { return <LegalPage slug="terms-of-use" testId="terms-of-use-page" fallbackTitle="Terms of Use" />; }
export function CancelMembership() { return <LegalPage slug="cancel-membership" testId="cancel-membership-page" fallbackTitle="Cancel Membership" />; }
export function DataDeletion() { return <LegalPage slug="data-deletion" testId="data-deletion-page" fallbackTitle="Data Deletion" />; }
